import { ref, computed } from 'vue';
import { Api } from '@/api/index';
import { toast } from 'vue-sonner';
import type { CustomFieldValueUpdatedPayload } from './types';

export const useControlledCustomFieldEditor = () => {
  const pendingChanges = ref<Map<number, CustomFieldValueUpdatedPayload>>(new Map());
  const isSaving = ref(false);

  const hasPendingChanges = computed(() => pendingChanges.value.size > 0);

  const trackChange = (event: CustomFieldValueUpdatedPayload) => {
    pendingChanges.value.set(event.custom_field_value.custom_field_id, event);
  };

  const clearChanges = () => {
    pendingChanges.value.clear();
  };

  const saveChanges = async (): Promise<boolean> => {
    if (!hasPendingChanges.value) {
      return true;
    }

    isSaving.value = true;

    const promises = Array.from(pendingChanges.value.values()).map(change =>
      Api.CustomFieldValue.updateCustomFieldValue({
        payload: {
          custom_fieldable_type: change.custom_field_value.custom_fieldable_type,
          custom_fieldable_id: change.custom_field_value.custom_fieldable_id,
          custom_field_id: change.custom_field_value.custom_field_id,
          value: change.custom_field_value.value,
        },
      })
    );

    const results = await Promise.all(promises);
    const errors = results.filter(r => r.status === 'error');

    isSaving.value = false;

    if (errors.length > 0) {
      toast.error('Some fields failed to save');
      return false;
    }

    toast.success('Custom fields saved');
    clearChanges();
    return true;
  };

  return {
    isSaving,
    trackChange,
    clearChanges,
    saveChanges,
  };
};
